import { AvatarDropdown, Footer, Question, SelectLang } from "@/components";
import { getCurrentUser } from "@/services/api/user";
import {
  handleErrorResponse,
  handleHttpError,
  handleTokenExpired,
} from "@/utils/errorHandler";
import { LinkOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type {
  AxiosRequestConfig,
  RequestConfig,
  RunTimeLayoutConfig,
} from "@umijs/max";
import { history, Link } from "@umijs/max";
import type { ApiResponse, User } from "types";
import defaultSettings from "../config/defaultSettings";
import AvatarName from "./components/AvatarName";
import { getApiBaseUrl } from "./utils/apiConfig";

const isDev = process.env.NODE_ENV === "development";
const loginPath = "/user/login";

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: User;
  loading?: boolean;
  fetchUserInfo?: () => Promise<User | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      // ============ 改用自定义的 getCurrentUser ============
      const response = await getCurrentUser();
      // 检查响应是否成功
      if (response.success && response.data) {
        return response.data;
      } else {
        // 如果获取失败，跳转到登录页
        console.error("获取用户信息失败:", response.errorMessage);
        handleTokenExpired();
        history.push(loginPath);
        return undefined;
      }
    } catch (error) {
      console.error("获取用户信息异常:", error);
      handleTokenExpired();
      history.push(loginPath);
      return undefined;
    }
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, "/user/register", "/user/register-result"].includes(
      location.pathname
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    
    avatarProps: {
      src: initialState?.currentUser?.profile?.picture,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return (
          <AvatarDropdown currentUser={initialState?.currentUser}>
            {avatarChildren}
          </AvatarDropdown>
        );
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.username,
    },
    footerRender: () => <Footer />,
    // menuRender: () => <div>bbs</div>,
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        // history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    // menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // ============ 基础配置 ============
  baseURL: getApiBaseUrl(),
  timeout: 30000,

  // ============ 请求前处理 ============
  requestInterceptors: [
    (config: AxiosRequestConfig) => {
      config.baseURL = getApiBaseUrl();
      // 添加认证令牌
      const token = localStorage.getItem("token");
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `${token}`,
        };
      }
      // 添加请求 ID 用于追踪
      config.headers = {
        ...config.headers,
        "X-Request-ID": `${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      };

      return config;
    },
  ],

  // ============ 响应处理 ============
  responseInterceptors: [
    (response: any) => {
      const { data, status } = response;
      // console.log("[GraphQL Response]", {
      //   status,
      //   hasErrors: data?.errors?.length > 0,
      // });
      // ============ 检查 Token 过期 ============
      if (status === 401) {
        handleTokenExpired();
        throw new Error("Token expired");
      }
      if (status === 403) {
        handleTokenExpired();
        throw new Error("Unauthorized");
      }
      
      // ============ 处理 GraphQL 响应错误 ============
      if (data?.errors && data?.errors.length > 0) {
        const error = data.errors[0];
        const errorMessage = error?.message || "GraphQL Error";
        // const errorCode = error?.code || 'GRAPHQL_ERROR';
        const errorCode =
          error?.extensions?.code || error?.code || "GRAPHQL_ERROR";
        const errorStatus = error?.extensions?.status;
        console.error("[GraphQL Error Details]", {
          errorMessage,
          errorCode,
          errorStatus,
          fullError: error,
        });
        // ============ 检查 GraphQL 中的 Token 过期错误 ============
        if (
          errorCode === "UNAUTHENTICATED" ||
          errorCode === "UNAUTHORIZED" ||
          errorStatus === 403 ||
          errorStatus === 401 ||
          errorMessage.includes("token") ||
          errorMessage.includes("unauthorized") ||
          errorMessage.includes("Unauthorized")
        ) {
          handleTokenExpired();
          throw new Error("Token expired");
        }

        const errorResponse: ApiResponse = {
          success: false,
          errorCode,
          errorMessage,
          showType: 2, // message.error
          traceId: response.headers["x-request-id"],
          host: response.headers["x-served-by"],
        };

        handleErrorResponse(errorResponse);
        throw errorResponse;
      }

      // ============ 处理标准 API 响应 ============
      if (data && typeof data === "object" && "success" in data) {
        const apiResponse = data as ApiResponse;

        // 请求失败
        if (!apiResponse.success) {
          // 添加追踪信息
          apiResponse.traceId =
            apiResponse.traceId || response.headers["x-request-id"];
          apiResponse.host =
            apiResponse.host || response.headers["x-served-by"];

          handleErrorResponse(apiResponse);
          throw apiResponse;
        }

        return apiResponse;
      }

      // 如果响应不是标准格式，直接返回
      return response;
    },
  ],

  // ============ 错误处理 ============
  errorConfig: {
    errorHandler: (error: any) => {
      if (error?.response.data.errors[0].extensions.code === 'UNAUTHORIZED' || error?.response.data.errors[0].extensions.status === 403)
      {
        handleTokenExpired();
        setTimeout(()=>history.push(loginPath),1000)
        return;
        // throw new Error("Token expired");
      }
      // 避免重复处理
      if (error?.message === 'Token expired') {
        return;
      }
      const { response, message: errorMessage } = error;

      // ============ 处理 Token 过期错误 ============
      if (response?.status === 401 || response?.status === 403) {
        console.error("[HTTP Auth Error] Status:", response.status);
        handleTokenExpired();
        throw error;
      }

      // ============ 处理 API 错误响应 ============
      if (error.success === false) {
        handleErrorResponse(error);
        throw error;
      }

      // ============ 处理 HTTP 状态码错误 ============
      if (response?.status) {
        handleHttpError(response.status);
        throw error;
      }

      // ============ 网络错误或其他异常 ============
      if (!response) {
        const errorResponse: ApiResponse = {
          success: false,
          errorCode: "NETWORK_ERROR",
          errorMessage: errorMessage || "网络连接失败，请检查网络设置",
          showType: 2,
        };
        handleErrorResponse(errorResponse);
        throw errorResponse;
      }

      throw error;
    },

    // 不同状态码的错误处理
    errorThrower: (res: any) => {
      throw res;
    },
  },
};
